import { z } from 'zod'

export const VENDOR_KEYS = ['meraki', 'cloudflare', 'extreme', 'intune', 'cradlepoint'] as const
export type VendorKey = (typeof VENDOR_KEYS)[number]

/** Same field catalog v1's VendorRow uses (secret fields come back masked and are fetched via reveal). */
export const VENDOR_FIELDS: Record<VendorKey, { nonSecret: string[]; secret: string[] }> = {
  meraki: { nonSecret: ['orgId', 'schemaStart', 'schemaStartDev'], secret: ['apiKey'] },
  cloudflare: { nonSecret: ['accountId', 'apiEmail', 'schemaStart', 'schemaStartDev'], secret: ['apiKey', 'apiToken'] },
  extreme: { nonSecret: ['orgId'], secret: ['apiUser', 'apiPassword'] },
  intune: { nonSecret: ['tenantId', 'clientId', 'groupIds'], secret: ['clientSecret'] },
  cradlepoint: { nonSecret: ['apiId', 'ecmApiId'], secret: ['apiKey', 'ecmApiKey'] },
}

export const VENDOR_LABELS: Record<VendorKey, string> = {
  meraki: 'Meraki',
  cloudflare: 'Cloudflare',
  extreme: 'Extreme',
  intune: 'Intune',
  cradlepoint: 'Cradlepoint',
}

/** Org / tenant settings are an open record keyed by vendor, each a record of (masked) field values + `enabled`. */
export type VendorSettings = Record<string, unknown>
export const vendorSettingsSchema = z.record(z.string(), z.unknown())

export const okSchema = z.object({ success: z.boolean() }).passthrough()
export const revealSchema = z.object({ success: z.boolean(), data: z.object({ value: z.string() }) })

/** Pure: the settings block for one vendor, or null when the vendor is unconfigured. */
export function vendorBlock(settings: VendorSettings | undefined, vendor: VendorKey): Record<string, unknown> | null {
  const block = settings?.[vendor]
  return block && typeof block === 'object' ? (block as Record<string, unknown>) : null
}

/** Pure: is the vendor enabled. */
export const vendorEnabled = (settings: VendorSettings | undefined, vendor: VendorKey): boolean =>
  vendorBlock(settings, vendor)?.enabled === true

/**
 * Pure: PATCH body for saving edited fields. Drops empty edits (an untouched masked secret must not overwrite the
 * stored value) and fields outside the vendor's catalog.
 */
export function vendorPatchBody(vendor: VendorKey, edits: Record<string, string>) {
  const allowed = new Set([...VENDOR_FIELDS[vendor].nonSecret, ...VENDOR_FIELDS[vendor].secret])
  const data: Record<string, string> = {}
  for (const [k, v] of Object.entries(edits)) if (allowed.has(k) && v !== '') data[k] = v
  return { vendor, data }
}

/** Pure: PATCH body for the enable toggle. */
export const vendorToggleBody = (vendor: VendorKey, enabled: boolean) => ({ vendor, data: { enabled } })
